import { memo, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { Box } from "@mui/material";
import { CapacityTracker } from "../CapacityTracker/CapacityTracker";
import { useCapacityTrackerListViewModel } from "./useCapacityTrackerListViewModel";

const splitCapacities = (capacities, chunkSize = 7) => {
  const result = [];

  for (let i = 0; i < capacities.length; i += chunkSize) {
    result.push(capacities.slice(i, i + chunkSize));
  }

  return result;
};

/**
 * CapacityTrackerList Component
 *
 * A component that displays a horizontally scrollable list of CapacityTracker components,
 * handling lazy loading of additional capacity data as the user scrolls.
 *
 * @component
 * @param {Object} props - The component props
 * @param {boolean} props.refresh - A flag to trigger reloading of capacities
 * @param {Object} props.selectedOrderTemplate - The currently selected order template for filtering capacities
 * @returns {JSX.Element} The rendered CapacityTrackerList component
 */
export const CapacityTrackerList = memo(
  ({ refresh, selectedOrderTemplate }) => {
    const { classes } = useStyles();
    const {
      listContainerRef,
      isLoading,
      debouncedIsLoading,
      summaries,
      capacities,
      getInitialCapacities,
      getCapacityList,
    } = useCapacityTrackerListViewModel();

    useEffect(() => {
      const handleScroll = (e) => {
        if (!listContainerRef.current || isLoading || debouncedIsLoading) {
          return;
        }
        const { scrollLeft, scrollWidth, clientWidth } = e.target;
        const remainingScroll = scrollWidth - scrollLeft - clientWidth;
        const threshold = clientWidth * 0.9;

        if (remainingScroll <= threshold) {
          getCapacityList();
        }
      };

      const container = listContainerRef.current;

      if (container) {
        container.addEventListener("scroll", handleScroll);
      }

      return () => {
        if (container) {
          container.removeEventListener("scroll", handleScroll);
        }
      };
    }, [isLoading, debouncedIsLoading]);

    const renderCapcities = () =>
      splitCapacities(summaries).map((summary, i) => (
        <Box key={i} className={classes.capacitiesContainer}>
          {summary.map((capacity) => (
            <CapacityTracker
              key={capacity.id}
              capacity={capacity}
              orderTemplateCapacities={capacities[capacity.date]}
              selectedOrderTemplate={selectedOrderTemplate}
            />
          ))}
        </Box>
      ));

    useEffect(() => {
      getInitialCapacities();
    }, [refresh]);

    return (
      <Box className={classes.container} data-testid="capacity-tracker-list">
        <Box ref={listContainerRef} className={classes.listContainer}>
          {renderCapcities()}
        </Box>
      </Box>
    );
  }
);

const useStyles = makeStyles()((theme) => ({
  container: {
    top: 0,
    width: "100%",
    backgroundColor: theme.palette.grey.accent,
  },
  listContainer: {
    display: "flex",
    gap: theme.spacing(2),
    overflowX: "auto",
    width: "100%",
  },
  capacitiesContainer: {
    display: "flex",
  },
}));
